import * as React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { colors } from "../../../utils/colors";
import { useTabsDataWithReactQuery } from "../../../hooks/useTabsDataWithReactQuery";
import { CustomTabPanel } from "./CustomTabPanel";
import { TabHeader } from "./TabHeader";
import { YardTabContent } from "./YardTabContent";
import { DevTabContent } from "./DevTabContent";

interface BasicTabsProps {
  token?: string;
}

export default function BasicTabs({
  token,
}: BasicTabsProps): React.JSX.Element {
  const [value, setValue] = React.useState(0);

  const {
    trucks,
    devTrucks,
    loading,
    permissionsLoading,
    availabilityFilter,
    hitOrMiss,
    setHitOrMiss,
    handleDeleteTruck: deleteTruck,
    handleUpdateTruck: updateTruck,
    handleUpdateTruckDirection,
    handleUpdateTruckLicensePlate,
    handleFilterChange,
    getYardName,
    isAdmin,
    refetchTrucks: fetchAndSetFilteredTrucks,
    trucksRefetching,
    trucksUpdatedAt,
  } = useTabsDataWithReactQuery({ token });

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setValue(newValue);
    if (newValue === 0 && !loading) {
      await fetchAndSetFilteredTrucks();
    }
  };

  if (permissionsLoading) {
    return (
      <Box
        sx={{
          width: "96%",
          borderRadius: "10px",
          backgroundColor: colors.cardBackground,
          marginTop: "50px",
          marginLeft: "2%",
          marginRight: "2%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "96%",
        borderRadius: "10px",
        backgroundColor: colors.cardBackground,
        marginTop: "50px",
        marginLeft: "2%",
        marginRight: "2%",
      }}
    >
      <Box sx={{ paddingX: "24px", paddingTop: "24px" }}>
        <TabHeader
          value={value}
          onChange={handleChange}
          isAdmin={isAdmin}
          yardName={getYardName()}
        />
      </Box>

      {loading ? (
        <CircularProgress sx={{ p: 5 }} />
      ) : (
        <>
          <CustomTabPanel value={value} index={0} token={token}>
            <YardTabContent
              trucks={trucks}
              token={token}
              deleteTruck={deleteTruck}
              updateTruck={updateTruck}
              updateEntryDirection={handleUpdateTruckDirection}
              updateLicensePlate={handleUpdateTruckLicensePlate}
              availabilityFilter={availabilityFilter}
              onFilterChange={handleFilterChange}
              lastUpdated={
                trucksUpdatedAt ? new Date(trucksUpdatedAt) : undefined
              }
              isRefetching={trucksRefetching}
            />
          </CustomTabPanel>

          {isAdmin && (
            <CustomTabPanel value={value} index={1} token={token}>
              <DevTabContent
                devTrucks={devTrucks}
                hitOrMiss={hitOrMiss}
                setHitOrMiss={setHitOrMiss}
                token={token}
                deleteTruck={deleteTruck}
                updateTruck={updateTruck}
                updateEntryDirection={handleUpdateTruckDirection}
                updateLicensePlate={handleUpdateTruckLicensePlate}
              />
            </CustomTabPanel>
          )}
        </>
      )}
    </Box>
  );
}
